// react_frontend/src/Components/Modules/UserSetting/EditEmployee.js
import React, { useState, useEffect, useRef, useContext } from "react";
import { MotherContext } from "../../../Context/MotherContext";
import Breadcrumb from "../../Partials/Breadcrumb";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";

import {
  Form,
  Button,
  SelectPicker,
  Message,
  Loader,
  Whisper,
  Tooltip,
  Schema,
  toaster,
} from "rsuite";
import "rsuite/dist/rsuite.min.css";

import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaUserShield,
  FaUserTie,
  FaCashRegister,
  FaClipboardList,
} from "react-icons/fa";

const { StringType } = Schema.Types;

const model = Schema.Model({
  name: StringType().isRequired("Name is required."),
  email: StringType()
    .isEmail("Please enter a valid email.")
    .isRequired("Email is required."),
  phone: StringType().addRule(
    (value) => !value || /^\d{10,15}$/.test(value),
    "Phone must be 10–15 digits."
  ),
  password: StringType().minLength(8, "Password must be at least 8 characters."),
  who_am_i: StringType().isRequired("Role is required."),
});

const EditUser = () => {
  const { id } = useParams();
  const { updateUserInList, user } = useContext(MotherContext);
  const navigate = useNavigate();
  const [formValue, setFormValue] = useState({});
  const [formError, setFormError] = useState({});
  const [loading, setLoading] = useState(false);
  const formRef = useRef();

  // Role options
  const roleOptions = [
    { label: "Master Admin", value: 1, icon: <FaUserShield /> },
    { label: "Admin", value: 2, icon: <FaUserTie /> },
    { label: "Account", value: 3, icon: <FaCashRegister /> },
    { label: "Sales Manager", value: 4, icon: <FaUser /> },
  ].filter((role) => {
    if (role.value === 1 && Number(user.role) !== 1) {
      return false;
    }
    return true;
  });



  // Fetch user data
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await AxiosAuthInstance.get(`users/${id}`);
        const user = response.data.user;

        setFormValue({
          name: user.name,
          email: user.email,
          phone: user.phone,
          who_am_i: Number(user.who_am_i), // ensure number
        });
      } catch (error) {
        console.error("Failed to fetch user:", error);
        toaster.push(
          <Message type="error" duration={5000}>
            Failed to load user data.
          </Message>,
          { placement: "topEnd" }
        );
      }
    };

    fetchUser();
  }, [id]);

  const handleSubmit = async () => {
    if (!formRef.current.check()) {
      return;
    }

    // Prevent non-master from setting master
    if (formValue.who_am_i === 1 && Number(user.role) !== 1) {
      toaster.push(
        <Message type="error" closable>
          Only Master Admin can assign Master Admin role.
        </Message>,
        { placement: "topEnd" }
      );
      return;
    }

    setLoading(true);
    try {
      const res = await AxiosAuthInstance.put(`users/${id}`, {
        name: formValue.name,
        email: formValue.email,
        phone: formValue.phone,
        password: formValue.password || undefined,
        who_am_i: formValue.who_am_i,
      });

      if (res.data.status) {
        updateUserInList({
          id: id,
          name: formValue.name,
          email: formValue.email,
          phone: formValue.phone,
          who_am_i: formValue.who_am_i,
          user_status: 1,
        });

        toaster.push(<Message type="success">Updated!</Message>, {
          placement: "topEnd",
        });
        navigate("/user_setting");
      }
    } catch (err) {
      const serverErrors = err.response?.data?.errors || {};
      const formattedErrors = Object.keys(serverErrors).reduce((acc, key) => {
        acc[key] = serverErrors[key][0];
        return acc;
      }, {});
      setFormError(formattedErrors);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/user_setting");
  };

  return (
    <>
      <Breadcrumb title="Edit User" />

      <div className="p-3">
        <div
          className="card shadow-sm border-0 mx-auto"
          style={{ maxWidth: "700px" }}
        >
          <div
            className="card-header d-flex justify-content-between align-items-center"
            style={{ backgroundColor: "#4351A3", color: "white" }}
          >
            <h5 className="mb-0">
              <FaUser className="me-2" />
              Edit User
            </h5>
            <Link to="/user_setting" className="text-white text-decoration-none">
              <Whisper placement="left" speaker={<Tooltip>User List</Tooltip>}>
                <Button size="sm" appearance="subtle" className="text-white">
                  <FaClipboardList /> List
                </Button>
              </Whisper>
            </Link>
          </div>

          <div className="card-body bg-light p-4">
            <Form
              ref={formRef}
              fluid
              formValue={formValue}
              formError={formError}
              onChange={setFormValue}
              onCheck={setFormError}
            >
              {/* Name */}
              <Form.Group controlId="name">
                <Form.ControlLabel>Name *</Form.ControlLabel>
                <Form.Control name="name" placeholder="Enter full name" />
                <Form.ErrorMessage name="name" />
              </Form.Group>

              {/* Email */}
              <Form.Group controlId="email">
                <Form.ControlLabel>Email Address *</Form.ControlLabel>
                <Form.Control
                  name="email"
                  type="email"
                  placeholder="user@example.com"
                />
                <Form.ErrorMessage name="email" />
              </Form.Group>

              {/* Phone */}
              <Form.Group controlId="phone">
                <Form.ControlLabel>Phone Number</Form.ControlLabel>
                <Form.Control name="phone" placeholder="e.g., 01712345678" />
                <Form.ErrorMessage name="phone" />
              </Form.Group>

              {/* Password */}
              <Form.Group controlId="password">
                <Form.ControlLabel>Password (Optional)</Form.ControlLabel>
                <Form.Control
                  name="password"
                  type="password"
                  placeholder="Leave blank to keep current password"
                />
                <Form.HelpText className="text-muted">
                  Min 8 characters. Leave blank to keep existing password.
                </Form.HelpText>
                <Form.ErrorMessage name="password" />
              </Form.Group>

              {/* Role */}
              <Form.Group controlId="who_am_i">
                <Form.ControlLabel>Role *</Form.ControlLabel>
                <Whisper
                  placement="right"
                  speaker={
                    <Tooltip style={{ maxWidth: 250 }}>
                      <strong>Role Descriptions:</strong>
                      <br />
                      <strong>Master Admin:</strong> Full access
                      <br />
                      <strong>Admin:</strong> Manage users & settings
                      <br />
                      <strong>Account:</strong> Financial operations
                      <br />
                      <strong>Sales Manager:</strong> Sales & customers
                    </Tooltip>
                  }
                >
                  <span style={{ cursor: "help" }}>
                    <Form.Control
                      name="who_am_i"
                      accepter={SelectPicker}
                      data={roleOptions}
                      placeholder="Select Role"
                      cleanable={false}
                      block
                    />
                  </span>
                </Whisper>
                <Form.ErrorMessage name="who_am_i" />
              </Form.Group>

              {/* Action Buttons */}
              <div className="mt-4 d-flex gap-3">
                <Button
                  appearance="primary"
                  color="blue"
                  size="md"
                  onClick={handleSubmit}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  {loading ? (
                    <>
                      <Loader size="sm" /> Updating...
                    </>
                  ) : (
                    "Update User"
                  )}
                </Button>

                <Button
                  className="border"
                  appearance="subtle"
                  color="red"
                  size="md"
                  onClick={handleCancel}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditUser;
